import { useCallback } from 'react';
import useHttpQuery from 'data/firestore/useHttpQuery';

export const GPT_MODELS_STALE_TIME = 5 * 60 * 1000;

export interface ResponderCapabilities {
    status: 'supported' | 'unsupported' | 'unverified';
    reasoningEfforts: string[];
    defaultReasoningEffort?: string;
    temperature: 'always' | 'none-only' | 'never' | 'unknown';
    endpoint: string;
}

interface GptModel {
    capabilities?: ResponderCapabilities;
    id: string;
    name: string;
    shutdown_date?: string | null;
}

interface GptModelsResponse {
    data: GptModel[];
}

const validateResponse = (response: GptModelsResponse): GptModelsResponse => {
    if (!Array.isArray(response?.data) || response.data.some((model) => (
        !model || typeof model.id !== 'string' || !model.id.trim()
        || typeof model.name !== 'string' || !model.name.trim()
        || (model.shutdown_date != null && typeof model.shutdown_date !== 'string')
    ))) {
        throw new Error('Invalid GPT model list');
    }
    return response;
};

const useGptModels = (responders = false) => {
    const result = useHttpQuery<GptModelsResponse>({
        pathname: responders ? '/models/responders' : '/models/gpt',
        server: process.env.REACT_APP_BASE_URL,
        queryOptions: {
            staleTime: GPT_MODELS_STALE_TIME,
            cacheTime: GPT_MODELS_STALE_TIME,
            refetchOnWindowFocus: false,
            retry: false,
            select: validateResponse,
        },
    });
    const {
        dataUpdatedAt, isError, isFetching, refetch,
    } = result;
    const refreshIfStale = useCallback(() => {
        if (!isFetching && (isError || Date.now() - dataUpdatedAt >= GPT_MODELS_STALE_TIME)) {
            // Focus and click can fire together; share any request already in progress.
            refetch({ cancelRefetch: false });
        }
    }, [dataUpdatedAt, isError, isFetching, refetch]);

    return { ...result, refreshIfStale };
};

export default useGptModels;
